'use client';

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useApolloClient } from '@apollo/client';
import { USER_QUERY } from '@app/apollo/queries';
import { getToken } from '@app/config/apollo/helpers';

export const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const client = useApolloClient();

  const getValidToken = useCallback(() => {
    try {
      const token = getToken()();
      if (!token || token.trim() === '' || token === 'null' || token === 'undefined') return null;
      return token;
    } catch (err) {
      console.warn('Error reading token in UserProvider', err);
      return null;
    }
  }, []);

  const token = getValidToken();
  const shouldFetch = !!token;

  const { data: fetchedData, loading, error, refetch } = useQuery(USER_QUERY, {
    skip: !shouldFetch,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: false,
    returnPartialData: true,
    onCompleted: (d) => {
      if (d?.me) {
        if (process.env.NODE_ENV !== 'production') console.count('USER_QUERY fetched (UserProvider)');
      }
    },
  });

  const user = useMemo(() => fetchedData?.me || null, [fetchedData?.me]);
  const isAuthenticated = useMemo(() => !!user && !!token, [user, token]);

  const updateUserInCache = useCallback((updatedUser) => {
    if (!updatedUser) return;
    try {
      client.writeQuery({ query: USER_QUERY, data: { me: updatedUser } });
    } catch (err) {
      console.warn('updateUserInCache failed', err);
    }
  }, [client]);

  const setUserInCache = useCallback((userData) => {
    if (!userData) return;
    try {
      client.writeQuery({ query: USER_QUERY, data: { me: userData } });
    } catch (err) {
      console.warn('setUserInCache failed', err);
    }
  }, [client]);

  const clearUserFromCache = useCallback(() => {
    try {
      client.writeQuery({ query: USER_QUERY, data: { me: null } });
      client.cache.evict({ fieldName: 'me' });
      client.cache.gc();
    } catch (err) {
      console.warn('clearUserFromCache failed', err);
    }
  }, [client]);

  const getCachedUser = useCallback(() => {
    try {
      const cached = client.readQuery({ query: USER_QUERY, errorPolicy: 'ignore' });
      return cached?.me || null;
    } catch (err) {
      return null;
    }
  }, [client]);

  const hasCachedUserData = useMemo(() => {
    try {
      const cached = client.readQuery({ query: USER_QUERY, errorPolicy: 'ignore' });
      return !!cached?.me;
    } catch (err) {
      return false;
    }
  }, [client]);

  const refreshUser = useCallback(() => {
    if (!token) return Promise.resolve();
    return refetch();
  }, [refetch, token]);

  const isUserManagerOrAdmin = useCallback((members) => {
    if (!user || !members) return false;
    return members.some(member =>
      (member?.role === 'Manager' || member?.role === 'Admin') && member?.user?.id === user.id
    );
  }, [user]);

  const isOrganiserOrInvitedAdmin = useCallback((opportunity, teamMembers = null) => {
    if (!user || !opportunity) return false;
    if (opportunity.user?.id === user.id) return true;

    const members = [
      teamMembers,
      opportunity.teamMembers,
      opportunity.organisation?.teamMembers,
    ].find(Array.isArray);

    if (!members) return false;

    return members.some(member =>
      (member?.role === 'Manager' || member?.role === 'Admin') &&
      member?.user?.id === user.id
    );
  }, [user]);

  const isOpportunityChatMember = useCallback((opportunity) => {
    if (!user || !opportunity?.chatGroup) return false;
    return opportunity.chatGroup.members?.some(member => member?.user?.id === user.id);
  }, [user]);

  const getUserRoleForEvent = useCallback((event) => {
    if (!event || !user) return null;
    if (event.user?.id === user.id) return 'organizer';
    const member = event.members?.find(m => m.user?.id === user.id);
    return member?.role?.toLowerCase() || null;
  }, [user]);

  const hasEventAdminAccess = useCallback((event, requiredRoles = ['organizer', 'admin', 'manager']) => {
    const userRole = getUserRoleForEvent(event);
    return requiredRoles.includes(userRole);
  }, [getUserRoleForEvent]);

  const isOrganizer = useCallback((event) => {
    if (!user || !event?.user?.id) return false;
    return event.user?.id === user.id;
  }, [user]);

  const contextValue = useMemo(() => ({
    loading,
    isAuthenticated,
    user,
    loggedInUser: user,
    hasCachedUserData,
    isUserManagerOrAdmin,
    isOrganiserOrInvitedAdmin,
    isOpportunityChatMember,
    getUserRoleForEvent,
    hasEventAdminAccess,
    isOrganizer,
    refreshUser,
    updateUserInCache,
    setUserInCache,
    clearUserFromCache,
    getCachedUser,
    error: error || null,
  }), [loading, isAuthenticated, user, hasCachedUserData, isUserManagerOrAdmin, isOrganiserOrInvitedAdmin, getUserRoleForEvent, hasEventAdminAccess, isOrganizer, refreshUser, updateUserInCache, setUserInCache, clearUserFromCache, getCachedUser, error]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUserContext must be used within UserProvider');
  return ctx;
};

